import { useState } from 'react';
import { SECURITY_ENGINE_LABEL, SEVERITIES, summarizeFindings, type Finding, type SecurityPlan, type StepResult } from '@1qa/shared';
import { ChevronRight, ShieldCheck } from '@/components/icons';
import { Badge, Card, CardBody, CardHeader, StatusPill } from '@/components/ui';
import { GRADE_CLASS, SEVERITY_TONE, STATUS_LABEL, confidenceWord, SEVERITY_LABEL } from '@/lib/security';
import clsx from 'clsx';

function FindingRow({ f }: { f: Finding }) {
  const [open, setOpen] = useState(false);
  const loc = f.location;
  return (
    <li className="py-1">
      <button type="button" onClick={() => setOpen((o) => !o)} className="flex w-full items-start gap-3 rounded-field px-2 py-2 text-left hover:bg-surface-raised/50">
        <ChevronRight className={clsx('mt-1 size-3 shrink-0 text-fg-muted transition', open && 'rotate-90')} />
        <Badge tone={SEVERITY_TONE[f.severity]}>{SEVERITY_LABEL[f.severity]}</Badge>
        <span className="min-w-0 flex-1">
          <span className="block text-sm font-medium text-fg">{f.title}</span>
          <span className="mt-0.5 block truncate text-xs text-fg-muted">
            {f.category}
            {f.cwe ? ` · ${f.cwe}` : ''} · {STATUS_LABEL[f.status]} · {confidenceWord(f.confidence)} confidence
          </span>
        </span>
      </button>
      {open && (
        <div className="ml-8 space-y-3 border-l border-border pb-3 pl-4 text-sm">
          {loc && (loc.url || loc.file || loc.parameter) && (
            <p className="font-mono text-xs break-all text-fg-secondary">
              {loc.method ? `${loc.method} ` : ''}
              {loc.url ?? loc.file}
              {loc.parameter ? ` · parameter: ${loc.parameter}` : ''}
            </p>
          )}
          <Section title="What it is">{f.description}</Section>
          {f.evidence && <Section title="Evidence (proof of concept)" mono>{f.evidence}</Section>}
          <Section title="How to fix it">{f.remediation}</Section>
          {f.references.length > 0 && (
            <div>
              <h5 className="text-xs font-semibold tracking-label text-fg-secondary uppercase">References</h5>
              <ul className="mt-1 space-y-0.5">
                {f.references.map((r) => (
                  <li key={r}>
                    <a href={r} target="_blank" rel="noreferrer" className="text-xs text-link break-all hover:underline">
                      {r}
                    </a>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}
    </li>
  );
}

/** One check the pentester ran, expandable to its own output/log. */
function CheckRow({ s }: { s: StepResult }) {
  const [open, setOpen] = useState(false);
  const hasLog = !!s.log;
  return (
    <li className="py-0.5">
      <button
        type="button"
        onClick={() => hasLog && setOpen((o) => !o)}
        disabled={!hasLog}
        className="flex w-full items-start gap-3 rounded-field px-2 py-2 text-left hover:bg-surface-raised/50 disabled:cursor-default"
      >
        <ChevronRight className={clsx('mt-1 size-3 shrink-0 text-fg-muted transition', open && 'rotate-90', !hasLog && 'invisible')} />
        <StatusPill status={s.status === 'PENDING' ? 'QUEUED' : s.status} />
        <span className="min-w-0 flex-1 font-mono text-xs break-all text-fg">{s.value}</span>
        {s.durationMs != null && <span className="mt-1 shrink-0 text-[10px] text-fg-muted">{Math.max(1, Math.round(s.durationMs / 1000))}s</span>}
      </button>
      {open && hasLog && (
        <pre className="mb-2 ml-8 max-h-80 overflow-auto rounded-field bg-surface-raised p-3 text-xs whitespace-pre-wrap text-fg-secondary">{s.log}</pre>
      )}
    </li>
  );
}

/** A collapsible category of audit items. */
function CheckGroup({ label, items }: { label: string; items: StepResult[] }) {
  const [open, setOpen] = useState(false);
  const failed = items.filter((s) => s.status === 'FAILED').length;
  const passed = items.filter((s) => s.status === 'PASSED').length;
  return (
    <li className="py-1">
      <button type="button" onClick={() => setOpen((o) => !o)} className="flex w-full items-center gap-3 rounded-field px-2 py-2 text-left hover:bg-surface-raised/50">
        <ChevronRight className={clsx('size-3 shrink-0 text-fg-muted transition', open && 'rotate-90')} />
        <span className="min-w-0 flex-1 text-sm font-semibold text-fg">{label}</span>
        <span className="shrink-0 text-xs text-fg-muted">
          {failed > 0 && <span className="font-semibold text-danger-text">{failed} failed · </span>}
          {passed} passed · {items.length} items
        </span>
      </button>
      {open && (
        <ul className="ml-6 divide-y divide-border border-l border-border pl-2">
          {items.map((s, i) => (
            <CheckRow key={i} s={s} />
          ))}
        </ul>
      )}
    </li>
  );
}

/**
 * While the scan is running (`active`) this shows a progress indicator and the tool activity running right now.
 * When done it is the result of the STANDARD AUDIT: every checklist item grouped by category with its verdict
 * (passed / failed / not tested), each expandable to its evidence. `steps` are the live progress or stored result.
 */
export function SecurityChecks({ steps, active }: { steps: StepResult[]; active?: boolean }) {
  const checks = steps.filter((s) => s.kind !== 'assert' || s.group);
  const grouped = !active && checks.some((s) => s.group);
  if (checks.length === 0 && !active) return null;

  if (grouped) {
    // Audit result: the checklist items grouped by category (first-seen order), with coverage counts, then the
    // commands the pentester actually ran (ungrouped `act` steps), each expandable to its output.
    const audit = checks.filter((s) => s.group);
    const commands = checks.filter((s) => !s.group);
    const failed = audit.filter((s) => s.status === 'FAILED').length;
    const passed = audit.filter((s) => s.status === 'PASSED').length;
    const assessed = failed + passed;
    const groups: { label: string; items: StepResult[] }[] = [];
    const byLabel = new Map<string, { label: string; items: StepResult[] }>();
    for (const s of audit) {
      const label = s.group ?? 'Other';
      let g = byLabel.get(label);
      if (!g) {
        g = { label, items: [] };
        byLabel.set(label, g);
        groups.push(g);
      }
      g.items.push(s);
    }
    return (
      <div className="space-y-4">
        <Card>
          <CardHeader
            title="Standard security audit"
            description={`${assessed} of ${audit.length} checks assessed · ${failed} failed, ${passed} passed, ${audit.length - assessed} not tested`}
          />
          <CardBody>
            <ul className="divide-y divide-border">
              {groups.map((g) => (
                <CheckGroup key={g.label} label={g.label} items={g.items} />
              ))}
            </ul>
          </CardBody>
        </Card>
        {commands.length > 0 && (
          <Card>
            <CardHeader title="Commands run" description={`${commands.length} command${commands.length === 1 ? '' : 's'} the pentester ran, each with its output`} />
            <CardBody>
              <ul className="divide-y divide-border">
                {commands.map((s, i) => (
                  <CheckRow key={i} s={s} />
                ))}
              </ul>
            </CardBody>
          </Card>
        )}
      </div>
    );
  }

  // Live: flat activity list + progress bar.
  const done = checks.filter((s) => s.status !== 'RUNNING' && s.status !== 'PENDING').length;
  const running = checks.find((s) => s.status === 'RUNNING');
  return (
    <Card>
      <CardHeader
        title={
          <span className="inline-flex flex-wrap items-center gap-2">
            Testing in progress <StatusPill status="RUNNING" />
          </span>
        }
        description={`${done} check${done === 1 ? '' : 's'} run so far${running ? ` · now running: ${running.value}` : ''}`}
      />
      <CardBody>
        <div className="mb-3 h-1.5 w-full overflow-hidden rounded-full bg-surface-raised">
          <div className="h-full w-1/3 animate-pulse rounded-full bg-primary" />
        </div>
        {checks.length === 0 ? (
          <p className="py-2 text-center text-sm text-fg-muted">The pentester is getting started…</p>
        ) : (
          <ul className="divide-y divide-border">
            {checks.map((s, i) => (
              <CheckRow key={i} s={s} />
            ))}
          </ul>
        )}
      </CardBody>
    </Card>
  );
}

function Section({ title, children, mono }: { title: string; children: string; mono?: boolean }) {
  return (
    <div>
      <h5 className="text-xs font-semibold tracking-label text-fg-secondary uppercase">{title}</h5>
      <p className={clsx('mt-1 whitespace-pre-wrap text-fg', mono ? 'font-mono text-xs break-all' : 'text-sm')}>{children}</p>
    </div>
  );
}

/**
 * A security scan's result: an executive summary (A–F grade, severity counts, headline anyone can read) and the
 * technical findings, each expandable to its evidence and fix. `summarizeFindings` is the same roll-up the API uses.
 */
export function SecurityReport({ findings, plan }: { findings: Finding[]; plan?: SecurityPlan | null }) {
  const sorted = [...findings].sort((a, b) => SEVERITIES.indexOf(a.severity) - SEVERITIES.indexOf(b.severity));
  const sum = summarizeFindings(findings);
  return (
    <div className="space-y-4">
      <Card>
        <CardBody className="flex flex-wrap items-center gap-6">
          <div className="flex items-center gap-4">
            <div className={clsx('grid size-16 place-items-center rounded-2xl bg-surface-raised text-3xl font-bold ring-1 ring-border ring-inset', GRADE_CLASS[sum.grade])}>
              {sum.grade}
            </div>
            <div>
              <div className="flex items-center gap-2 text-sm font-semibold text-fg">
                <ShieldCheck className="size-4 text-fg-muted" />
                Security grade
              </div>
              <p className="mt-0.5 max-w-prose text-sm text-fg-secondary">{sum.headline}</p>
              {plan && <p className="mt-0.5 text-xs text-fg-muted">{SECURITY_ENGINE_LABEL[plan.engine ?? 'SHANNON']} · {plan.profile === 'DEEP' ? 'Deep scan' : 'Quick scan'} of {plan.targetUrl}</p>}
            </div>
          </div>
          <div className="ml-auto flex flex-wrap gap-2">
            {SEVERITIES.map((s) => (
              <div key={s} className="min-w-14 rounded-field bg-surface-raised px-3 py-2 text-center ring-1 ring-border ring-inset">
                <div className="text-lg font-semibold text-fg">{sum.counts[s]}</div>
                <div className="text-[10px] tracking-label text-fg-muted uppercase">{SEVERITY_LABEL[s]}</div>
              </div>
            ))}
          </div>
        </CardBody>
      </Card>

      <Card>
        <CardBody>
          {sorted.length === 0 ? (
            <p className="py-4 text-center text-sm text-fg-muted">No issues were proven in this scan.</p>
          ) : (
            <ul className="divide-y divide-border">
              {sorted.map((f) => (
                <FindingRow key={f.id} f={f} />
              ))}
            </ul>
          )}
        </CardBody>
      </Card>
    </div>
  );
}
